import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Calendar, Clock, ArrowRight, CheckCircle2, ShieldCheck, MapPin,
  Search, PiggyBank, Lock, RefreshCw, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../components/ui/accordion';

// Orlando Sosa Ruiz personalized HealthSherpa shopping experience — attribution preserved.
const HEALTHSHERPA_URL =
  'https://www.healthsherpa.com/marketplace/zip_code?_agent_id=orlando-sosa-ruiz-w8szbq';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const stepIcons = [MapPin, Search, PiggyBank, Lock];

const ACCENT_CLASS =
  'bg-sky-500 hover:bg-sky-600 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300';

const QuoteButton = ({ label, className = 'h-14 px-10 text-base', testid }) => (
  <a href={HEALTHSHERPA_URL} target="_blank" rel="noopener noreferrer" className="inline-block">
    <Button className={`${ACCENT_CLASS} ${className}`} data-testid={testid}>
      {label}
      <ArrowRight className="ml-2 h-4 w-4" />
    </Button>
  </a>
);

export const OpenEnrollmentPage = () => {
  const { t, i18n } = useTranslation();
  const dateItems = t('openEnrollment.dateItems', { returnObjects: true });
  const steps = t('openEnrollment.steps', { returnObjects: true });
  const renewItems = t('openEnrollment.renewItems', { returnObjects: true });
  const faqs = t('openEnrollment.faqs', { returnObjects: true });

  useEffect(() => {
    document.title = `${t('openEnrollment.metaTitle')} · Calyvra Insurance`;
    const desc = t('openEnrollment.metaDescription');
    let meta = document.querySelector('meta[name="description"]');
    const prev = meta ? meta.getAttribute('content') : null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', desc);

    // FAQ structured data (JSON-LD) for ACA Open Enrollment 2027 SEO.
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'oe-faq-jsonld';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: (Array.isArray(faqs) ? faqs : []).map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a }
      }))
    });
    document.getElementById('oe-faq-jsonld')?.remove();
    document.head.appendChild(ld);

    return () => {
      document.getElementById('oe-faq-jsonld')?.remove();
      if (prev && meta) meta.setAttribute('content', prev);
    };
  }, [t, i18n.language, faqs]);

  return (
    <div className="overflow-hidden" data-testid="open-enrollment-page">
      {/* HERO */}
      <section className="gradient-hero py-20 lg:py-28" data-testid="oe-hero">
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={fadeInUp} className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-600 rounded-full text-sm font-medium mb-6">
              <Calendar className="h-4 w-4" />
              {t('openEnrollment.heroBadge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              {t('openEnrollment.heroTitle')}
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">{t('openEnrollment.heroDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <QuoteButton label={t('openEnrollment.heroPrimaryCta')} className="h-14 px-10 text-base" testid="oe-primary-cta" />
              <Link to="/contact">
                <Button variant="outline" className="btn-secondary h-14 px-10" data-testid="oe-secondary-cta">
                  {t('openEnrollment.heroSecondaryCta')}
                </Button>
              </Link>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500">
              <ShieldCheck className="h-4 w-4 text-sky-500" />
              {t('openEnrollment.heroTrust')}
            </div>
          </motion.div>
        </div>
      </section>

      {/* KEY DATES */}
      <section className="section-spacing bg-white" data-testid="oe-dates">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('openEnrollment.datesTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('openEnrollment.datesSubtitle')}</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {dateItems.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl border border-slate-100 card-shadow"
                data-testid={`oe-date-${index}`}
              >
                <div className="h-12 w-12 bg-sky-50 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="h-6 w-6 text-sky-500" />
                </div>
                <p className="text-sm font-semibold text-sky-600 mb-1">{item.label}</p>
                <p className="text-xl font-bold text-slate-900 mb-2">{item.value}</p>
                <p className="text-slate-600 text-sm">{item.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW ENROLLMENT WORKS */}
      <section className="section-spacing gradient-subtle" data-testid="oe-steps">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-sky-600 rounded-full text-sm font-medium mb-4 border border-slate-100">
              <CheckCircle2 className="h-4 w-4" />
              {t('openEnrollment.stepsBadge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('openEnrollment.stepsTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('openEnrollment.stepsSubtitle')}</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-12">
            {steps.map((step, index) => {
              const Icon = stepIcons[index] || CheckCircle2;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative bg-white p-6 rounded-2xl border border-slate-100 card-shadow"
                  data-testid={`oe-step-${index}`}
                >
                  <span className="absolute -top-3 -left-3 h-8 w-8 rounded-full bg-sky-500 text-white text-sm font-bold flex items-center justify-center shadow">
                    {index + 1}
                  </span>
                  <div className="h-12 w-12 bg-sky-50 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-sky-500" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center">
            <QuoteButton label={t('openEnrollment.stepsCta')} className="h-14 px-10 text-base" testid="oe-steps-cta" />
          </div>
        </div>
      </section>

      {/* EXISTING MARKETPLACE MEMBERS */}
      <section className="section-spacing bg-white" data-testid="oe-renew">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-600 rounded-full text-sm font-medium mb-4">
                <RefreshCw className="h-4 w-4" />
                {t('openEnrollment.renewBadge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('openEnrollment.renewTitle')}</h2>
              <p className="text-lg text-slate-700 font-medium mb-4">{t('openEnrollment.renewLead')}</p>
              <p className="text-slate-600 mb-6">{t('openEnrollment.renewText')}</p>
              <QuoteButton label={t('openEnrollment.renewCta')} className="h-12 px-8" testid="oe-renew-cta" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-2xl p-8 border border-slate-100"
            >
              <div className="space-y-4">
                {renewItems.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-spacing gradient-subtle" data-testid="oe-faq">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-sky-600 rounded-full text-sm font-medium mb-4 border border-slate-100">
              <HelpCircle className="h-4 w-4" />
              {t('openEnrollment.faqTitle')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('openEnrollment.faqTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('openEnrollment.faqSubtitle')}</p>
          </motion.div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={faq.q}
                  value={`faq-${index}`}
                  className="bg-white rounded-xl border border-slate-100 px-6 card-shadow"
                  data-testid={`oe-faq-${index}`}
                >
                  <AccordionTrigger className="text-left text-slate-900 font-semibold hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-spacing bg-slate-900" data-testid="oe-final-cta">
        <div className="container-custom text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('openEnrollment.ctaTitle')}</h2>
            <p className="text-lg text-slate-300 mb-8">{t('openEnrollment.ctaText')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <QuoteButton label={t('openEnrollment.ctaPrimary')} className="h-14 px-10 text-base" testid="oe-final-primary-cta" />
              <Link to="/contact">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 h-14 px-10 rounded-full font-semibold" data-testid="oe-final-secondary-cta">
                  {t('openEnrollment.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="py-8 bg-white border-t border-slate-100" data-testid="oe-disclaimer">
        <div className="container-custom">
          <p className="text-xs text-slate-400 max-w-4xl mx-auto text-center leading-relaxed">
            {t('openEnrollment.disclaimer')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default OpenEnrollmentPage;
